import type {ReactNode} from 'react';
import {useTranslation} from 'react-i18next';
import {VisuallyHidden} from '@radix-ui/react-visually-hidden';
import {Dialog, DialogContent, DialogDescription, DialogTitle} from '../dialog';
import {Drawer, DrawerContent, DrawerDescription} from '../drawer';
import {useScreenResize} from '../hooks';
import {DEVICE} from '../lib/device';
import {cn} from '../lib/cn';
import {isMobileModalModeAvailable} from '../lib/runtimeSettings';

type ResponsiveSheetProps = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  dialogClassName?: string;
  drawerClassName?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  showCloseButton?: boolean;
  showDrawerHandle?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  disableDrag?: boolean;
  onOpenAutoFocus?: (event: Event) => void;
  onCloseAutoFocus?: (event: Event) => void;
};

function ResponsiveSheet({
  open,
  onClose,
  title,
  description,
  children,
  className,
  contentClassName,
  dialogClassName,
  drawerClassName,
  titleClassName,
  descriptionClassName,
  showCloseButton = true,
  showDrawerHandle = true,
  closeOnOverlayClick = false,
  closeOnEscape = true,
  disableDrag = false,
  onOpenAutoFocus,
  onCloseAutoFocus,
}: Readonly<ResponsiveSheetProps>) {
  const {t} = useTranslation();
  const {isMatch: isMobile} = useScreenResize(DEVICE.tablet);
  const isMobileMode = isMobile && isMobileModalModeAvailable();

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      onClose();
    }
  };

  const handleInteractOutside = (event: Event) => {
    if (!closeOnOverlayClick) {
      event.preventDefault();
    }
  };

  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    if (!closeOnEscape) {
      event.preventDefault();
    }
  };

  const content = (
    <div
      className={cn(
        'flex w-full flex-col px-6 pb-6 pt-3 sm:px-8 sm:pb-8 sm:pt-10',
        isMobileMode && !showDrawerHandle && 'pt-1.5',
        contentClassName,
      )}
    >
      {title ? (
        <DialogTitle className={cn('text-center mb-3', titleClassName)}>
          {title}
        </DialogTitle>
      ) : (
        <VisuallyHidden asChild>
          <DialogTitle>{description ?? t('dialog')}</DialogTitle>
        </VisuallyHidden>
      )}
      {description ? (
        <p
          aria-hidden="true"
          className={cn(
            'text-center text-[16px] leading-6 text-[var(--primary)]/70',
            descriptionClassName,
          )}
        >
          {description}
        </p>
      ) : null}
      {children}
    </div>
  );

  if (isMobileMode) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerContent
          onClose={onClose}
          showHandle={showDrawerHandle}
          closeOnOverlayClick={closeOnOverlayClick}
          disableDrag={disableDrag}
          lockScroll={false}
          onEscapeKeyDown={handleEscapeKeyDown}
          onOpenAutoFocus={onOpenAutoFocus}
          onCloseAutoFocus={onCloseAutoFocus}
          className={cn(className, drawerClassName)}
        >
          {description ? (
            <DrawerDescription className="sr-only">{description}</DrawerDescription>
          ) : null}
          {content}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        showCloseButton={showCloseButton}
        onPointerDownOutside={handleInteractOutside}
        onInteractOutside={handleInteractOutside}
        onEscapeKeyDown={handleEscapeKeyDown}
        onOpenAutoFocus={onOpenAutoFocus}
        onCloseAutoFocus={onCloseAutoFocus}
        className={cn('max-w-[560px] border-0 p-0 shadow-xl', className, dialogClassName)}
        lockScroll={false}
      >
        {description ? (
          <DialogDescription className="sr-only">{description}</DialogDescription>
        ) : null}
        {content}
      </DialogContent>
    </Dialog>
  );
}

export {ResponsiveSheet};
export type {ResponsiveSheetProps};
